import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@stores/translationStore';
import PageHeader from '@components/PageHeader';
import PageSpinner from '@components/PageSpinner';
import {
  getDocuments, uploadDocument, deleteDocument, getDocumentRooms, moveDocument,
  type ReservationDocument, type DocumentRoom,
} from '@services/precheckinApi';

/**
 * Documents — passport / ID upload before arrival. This was the pre-check-in form's documents step;
 * it is now a portal module on the stay's own session. A document staff have verified, or that has
 * been attached in the PMS, is locked and cannot be removed.
 */
export default function DocumentsPage() {
  const { hotelSlug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = (key: string, fallback: string) => (t(key) !== key ? t(key) : fallback);

  const fileRef = useRef<HTMLInputElement>(null);
  const [docs, setDocs] = useState<ReservationDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docType, setDocType] = useState('passport');
  // The main booker files documents for the rooms they look after too: whose document is this?
  const [rooms, setRooms] = useState<DocumentRoom[]>([]);
  const [forRoom, setForRoom] = useState('');

  const docTypes = [
    { value: 'passport', label: tr('documents.passport', 'Passport') },
    { value: 'id', label: tr('documents.idCard', 'ID card') },
    { value: 'visa', label: tr('documents.visa', 'Visa') },
    { value: 'other', label: tr('documents.other', 'Other') },
  ];

  const reload = () => getDocuments().then(setDocs);

  useEffect(() => {
    reload()
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    getDocumentRooms().then(setRooms).catch(() => setRooms([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roomName = (r: DocumentRoom) => r.own ? tr('documents.mine', 'Me (my room)') : (r.label ?? '');

  const onMove = async (doc: ReservationDocument, target: string) => {
    setError(null);
    try {
      await moveDocument(doc.id, target);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('documents.moveFailed', 'Could not move the document.'));
    }
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const target = rooms.find((r) => r.reservationId === forRoom && !r.own)?.reservationId;
      await uploadDocument(file, docType, target);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('documents.uploadFailed', 'Upload failed.'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (doc: ReservationDocument) => {
    setError(null);
    try {
      await deleteDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('documents.removeFailed', 'Could not remove the document.'));
    }
  };

  if (loading) return <PageSpinner />;

  const home = () => navigate(hotelSlug ? `/${hotelSlug}/welcome` : '/welcome');

  return (
    <div className="page-content">
      <PageHeader
        title={tr('documents.title', 'Documents')}
        subtitle={tr('documents.subtitle', 'Passport or ID for everyone registering')}
        leading={
          <button className="btn btn-link text-white p-0" onClick={home} aria-label={tr('common.back', 'Back')}>←</button>
        }
      />

      <div className="py-3">
        <p className="text-muted small">
          {tr('documents.hint', 'JPG, PNG or PDF, up to 15 MB.')}
        </p>

        {rooms.length > 1 && (
          <div className="mb-2">
            <label className="form-label small text-muted mb-1">{tr('documents.forWhom', 'Whose document is this?')}</label>
            <select className="form-select" value={forRoom} onChange={(e) => setForRoom(e.target.value)}>
              {rooms.map((r) => <option key={r.reservationId} value={r.own ? '' : r.reservationId}>{roomName(r)}</option>)}
            </select>
          </div>
        )}

        <div className="d-flex gap-2 mb-3">
          <select className="form-select" value={docType} onChange={(e) => setDocType(e.target.value)}>
            {docTypes.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <button type="button" className="btn btn-primary text-nowrap" disabled={busy}
            onClick={() => fileRef.current?.click()}>
            {busy ? tr('documents.uploading', 'Uploading…') : `+ ${tr('documents.add', 'Add file')}`}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={onPick} style={{ display: 'none' }} />
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {docs.length === 0 ? (
          <p className="text-muted fst-italic py-3">{tr('documents.none', 'No documents uploaded yet.')}</p>
        ) : (
          <ul className="list-group">
            {docs.map((d) => (
              <li key={d.id} className="list-group-item d-flex justify-content-between align-items-center gap-2">
                <div style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-truncate">{d.fileName}</div>
                  <div className="small text-muted">
                    {docTypes.find((x) => x.value === d.documentType)?.label ?? d.documentType}
                    {d.verificationStatus === 'Verified' && ` · ${tr('documents.verified', 'verified')}`}
                    {d.roomLabel && ` · ${d.roomLabel}`}
                  </div>
                  {!d.locked && rooms.length > 1 && (
                    <select className="form-select form-select-sm mt-1" value=""
                      onChange={(e) => e.target.value && onMove(d, e.target.value)}>
                      <option value="">{tr('documents.moveTo', 'Move to…')}</option>
                      {rooms.filter((r) => r.reservationId !== d.reservationId).map((r) => (
                        <option key={r.reservationId} value={r.reservationId}>{roomName(r)}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  {d.viewUrl && (
                    <a className="btn btn-sm btn-outline-secondary" href={d.viewUrl} target="_blank" rel="noopener noreferrer">
                      {tr('documents.view', 'View')}
                    </a>
                  )}
                  {d.locked
                    ? <span className="small text-muted">{tr('documents.locked', 'locked')}</span>
                    : <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(d)}>{tr('documents.remove', 'Remove')}</button>}
                </div>
              </li>
            ))}
          </ul>
        )}

        <button className="btn btn-primary w-100 mt-4" onClick={home}>{tr('common.done', 'Done')}</button>
      </div>
    </div>
  );
}
